// src/components/pages/UserProfile.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Button,
  Tooltip, // Import Tooltip from MUI
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  fetchUserProfile,
  fetchUserChallenges,
  fetchUserSubmissions,
  fetchUserMembershipsFromFirestore, // Ensure this is imported
} from "../../services/profileService";
// import ProfileTabs from "../organisms/Profile/ProfileTabs";
import ProfileHeader from "../organisms/Profile/ProfileHeader";
import ProfileChallengesSection from "../organisms/Profile/ProfileChallengesSection";
import ProfileSubmissionsSection from "../organisms/Profile/ProfileSubmissionsSection";
import { useTheme } from "styled-components";
import CustomCard from "../molecules/ChallengeCreator/CustomCard";
import { useAuth } from "../../context/AuthContext"; // Updated import path
import { formatDate, calculateRenewDate } from "../../utils/dateUtils"; // Import helper functions

const UserProfile = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser, loading: authLoading, error: authError } = useAuth();

  // Local state for profile data
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [memberships, setMemberships] = useState([]); // Local state for memberships
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchProfilesAndChallenges = async () => {
      setLoading(true);
      try {
        const userToFetch = profileId || (currentUser && currentUser.uid);
        console.log(`User to Fetch UID: ${userToFetch}`); // Debugging Log

        if (userToFetch) {
          let fetchedProfile;
          if (profileId) {
            // Fetch another user's profile
            fetchedProfile = await fetchUserProfile(userToFetch);
            console.log(
              `Fetched Profile for UID ${userToFetch}:`,
              fetchedProfile
            ); // Debugging Log
          } else {
            // Fetch current user's profile
            fetchedProfile = await fetchUserProfile(currentUser.uid);
            console.log("Fetched Current User's Profile:", fetchedProfile); // Debugging Log
          }
          setUserProfile(fetchedProfile);

          // Fetch challenges created by the target user
          const fetchedChallenges = await fetchUserChallenges(
            userToFetch,
            currentUser?.uid // Assuming currentUser.uid is needed
          );
          console.log("Fetched Challenges:", fetchedChallenges); // Debugging Log
          setChallenges(fetchedChallenges.slice(0, 3));

          // Fetch submissions created by the target user
          const fetchedSubmissions = await fetchUserSubmissions(
            userToFetch,
            currentUser?.uid // Assuming currentUser.uid is needed
          );
          console.log("Fetched Submissions:", fetchedSubmissions); // Debugging Log
          // setSubmissions(fetchedSubmissions.slice(0, 9)); // Limit to 9 items
          setSubmissions(fetchedSubmissions);

          // Determine if viewing own profile
          const isOwnProfile = !profileId || profileId === currentUser.uid;
          console.log("isOwnProfile:", isOwnProfile);

          if (isOwnProfile && currentUser) {
            console.log("Fetching memberships for own profile.");
            const fetchedMemberships = await fetchUserMembershipsFromFirestore(
              currentUser.uid
            );
            console.log("Fetched Memberships:", fetchedMemberships); // Debugging Log
            setMemberships(fetchedMemberships);
          } else {
            console.log(
              "Not fetching memberships (profileId exists or no currentUser)."
            );
            setMemberships([]); // Clear memberships if viewing others
          }
        } else {
          console.log("No userToFetch found. Redirecting to sign-in.");
          navigate("/challenge/signin");
        }
      } catch (err) {
        console.error("Error fetching profile or challenges data:", err);
        setError("Error fetching profile or challenges data.");
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if authentication is not loading
    if (!authLoading) {
      fetchProfilesAndChallenges();
    }
  }, [profileId, navigate, currentUser, authLoading]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle global auth loading and error states
  if (authLoading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <CircularProgress
            sx={{
              color: theme.colors.red,
            }}
          />
        </Box>
      </Container>
    );
  }

  if (authError) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <Typography variant="h6" color="error">
            {authError.message || "Authentication Error"}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle loading state for profile, challenges, etc.
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <CircularProgress
            sx={{
              color: theme.colors.red,
            }}
          />
        </Box>
      </Container>
    );
  }

  // Handle error state for profile, challenges, etc.
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle case where no userProfile is found and no profileId is provided
  if (!userProfile && !profileId) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            You are not signed in
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/challenge/signin")}
          >
            Sign In
          </Button>
        </Box>
      </Container>
    );
  }

  // Extract first name from userProfile displayName
  const displayName = userProfile?.displayName || "User";
  const firstName = displayName.split(" ")[0];

  // Determine if viewing own profile for rendering memberships
  const isOwnProfile = !profileId || profileId === currentUser.uid;
  console.log("Rendering memberships:", isOwnProfile, memberships);

  return (
    <Container
      sx={{
        mt: 4,
        maxWidth: {
          xs: "xs",
          sm: "sm",
          md: "md",
          lg: "lg",
          xl: "xl",
        },
      }}
    >
      {/* Conditionally render ProfileTabs if needed */}
      {/* {!profileId && (
        <ProfileTabs activeTab={activeTab} handleTabChange={handleTabChange} />
      )} */}

      <Box>
        {activeTab === 0 && (
          <Box>
            <ProfileHeader
              userProfile={userProfile}
              currentUser={currentUser}
              theme={theme}
            />
            <Box sx={{ pb: 4 }} />

            {/* Display Memberships if viewing own profile */}
            {isOwnProfile && (
              <CustomCard title="My Memberships">
                {memberships.length > 0 ? (
                  memberships.map((membership) => {
                    // Use helper functions for formatting
                    const formattedMemberSince = formatDate(
                      membership.createdAt
                    );
                    const formattedRenewsOn = calculateRenewDate(
                      membership.createdAt
                    );

                    return (
                      <Box
                        key={membership.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column", // Change to column for better layout
                          mb: 1,
                          pb: 1,
                          borderBottom: `1px solid ${theme.colors.palePurple}`,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "basic-sans",
                            color: theme.colors.lightGray,
                            mb: 0.5,
                          }}
                        >
                          <strong>Membership:</strong>{" "}
                          {membership.membershipName}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "basic-sans",
                            color: theme.colors.lightGray,
                            mb: 0.5,
                          }}
                        >
                          <strong>Status:</strong> {membership.status}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "basic-sans",
                            color: theme.colors.lightGray,
                            mb: 0.5,
                          }}
                        >
                          <strong>Member since:</strong> {formattedMemberSince}
                        </Typography>
                        {/* Conditionally render "Renews on" if status is active */}
                        {membership.status.toLowerCase() === "active" && (
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: "basic-sans",
                              color: theme.colors.lightGray,
                              display: "flex",
                              alignItems: "center", // Vertically center the items
                            }}
                          >
                            <strong>Renews on:&nbsp; </strong> {formattedRenewsOn}
                            <Tooltip
                              title='1. Log in to your PayPal account. 2. Access your settings by clicking the gear icon in the top-right corner. 3. Navigate to the "Payments" tab. 4. Click on "Manage Automatic Payments." 5. Select "Doopus, LLC" from the list of active merchants. 6. Click "Cancel" to terminate the automatic payment agreement.'
                              arrow
                            >
                              <HelpOutlineIcon
                                fontSize="small"
                                sx={{
                                  ml: 0.5, // Small left margin for spacing
                                  color: theme.colors.lightGray,
                                  cursor: "pointer", // Change cursor to pointer on hover
                                }}
                              />
                            </Tooltip>
                          </Typography>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigate("/challenge/memberships/cap")}
                      sx={{
                        backgroundColor: theme.colors.blue,
                        color: theme.colors.white,
                        fontFamily: "basic-sans",
                        fontWeight: "700",
                        textTransform: "none",
                        padding: "10px 20px",
                        borderRadius: "50px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: theme.colors.green,
                          boxShadow: "none",
                        },
                      }}
                    >
                      Join the Creator's Alliance Program
                    </Button>
                  </Box>
                )}
              </CustomCard>
            )}

            {/* Pass profileId to ProfileChallengesSection */}
            <ProfileChallengesSection
              firstName={firstName}
              profileId={profileId} // Pass the profileId here
              challenges={challenges}
              theme={theme}
            />

            {/* New Submissions Section */}
            {submissions.length > 0 && (
              <ProfileSubmissionsSection
                firstName={firstName}
                profileId={profileId}
                submissions={submissions}
                theme={theme}
              />
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              My Purchases
            </Typography>
            <Typography variant="body1" align="center">
              Display user's purchase information here.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default UserProfile;
